import { useEffect, useRef } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import {
  TRANSFER_LINE_CHANGED_BROADCAST_EVENT,
  TRANSFER_PICKS_REALTIME_TOPIC,
  TRANSFER_SCAN_BROADCAST_EVENT,
  parseTransferLineChange,
  parseTransferLineEventInsert,
  parseTransferScanInsert,
  transferScanRealtimeTopic,
  type TransferLineChangeBroadcast,
} from '@/shared/utils/inventoryTransferScan';
import { getSupabaseRealtimeClient } from '../services/supabaseRealtimeClient';

interface UseTransferScanRealtimeParams {
  readonly transferRequestId: string;
  readonly enabled?: boolean;
  readonly onScan: (lineId: string) => void;
  readonly onLineChange?: (change: TransferLineChangeBroadcast) => void;
  readonly onLineEvent?: (event: { readonly lineId: string; readonly eventId: string }) => void;
}

function subscribeChannel(
  channel: RealtimeChannel,
  label: string,
): void {
  channel.subscribe((status: string) => {
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      console.warn(`[inventory-transfer] ${label} channel error:`, status);
    }
  });
}

/**
 * Applies scan events for one transfer. Broadcast is its own channel so a
 * postgres_changes failure cannot drop the live push. Both paths are idempotent.
 */
export function useTransferScanRealtime({
  transferRequestId,
  enabled = true,
  onScan,
  onLineChange,
  onLineEvent,
}: UseTransferScanRealtimeParams): void {
  const onScanRef = useRef(onScan);
  const onLineChangeRef = useRef(onLineChange);
  const onLineEventRef = useRef(onLineEvent);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    onLineChangeRef.current = onLineChange;
  }, [onLineChange]);

  useEffect(() => {
    onLineEventRef.current = onLineEvent;
  }, [onLineEvent]);

  useEffect(() => {
    if (!enabled || transferRequestId.trim() === '') {
      return undefined;
    }

    const realtimeClient = getSupabaseRealtimeClient();
    if (!realtimeClient) {
      return undefined;
    }

    const topic = transferScanRealtimeTopic(transferRequestId);
    const apply = (message: unknown): void => {
      const parsed = parseTransferScanInsert(message);
      if (parsed == null || parsed.transferRequestId !== transferRequestId) {
        return;
      }
      onScanRef.current(parsed.lineId);
    };

    const broadcastChannel: RealtimeChannel = realtimeClient.channel(topic, {
      config: { broadcast: { self: true } },
    });
    broadcastChannel.on('broadcast', { event: TRANSFER_SCAN_BROADCAST_EVENT }, apply);
    broadcastChannel.on('broadcast', { event: TRANSFER_LINE_CHANGED_BROADCAST_EVENT }, (message: unknown) => {
      const parsed = parseTransferLineChange(message);
      if (parsed == null || parsed.transferRequestId !== transferRequestId) return;
      onLineChangeRef.current?.(parsed);
    });
    subscribeChannel(broadcastChannel, 'broadcast');

    const changesChannel: RealtimeChannel = realtimeClient.channel(`${topic}-changes`);
    changesChannel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'inventory',
        table: 'transfer_request_scans',
        filter: `transfer_request_id=eq.${transferRequestId}`,
      },
      apply,
    );
    subscribeChannel(changesChannel, 'postgres_changes');

    const lineEventsChannel: RealtimeChannel = realtimeClient.channel(`${topic}-line-events`);
    lineEventsChannel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'inventory',
        table: 'transfer_request_line_events',
      },
      (message: unknown) => {
        const parsed = parseTransferLineEventInsert(message);
        if (parsed == null) return;
        onLineEventRef.current?.(parsed);
      },
    );
    subscribeChannel(lineEventsChannel, 'line-events');

    return () => {
      realtimeClient.removeChannel(broadcastChannel).catch(() => {
        // no-op cleanup
      });
      realtimeClient.removeChannel(changesChannel).catch(() => {
        // no-op cleanup
      });
      realtimeClient.removeChannel(lineEventsChannel).catch(() => {
        // no-op cleanup
      });
    };
  }, [enabled, transferRequestId]);
}

/** Live pick-list edits for every open transfer, including the transfers list. */
export function useInventoryTransferPicksRealtime(
  onLineChange: (change: TransferLineChangeBroadcast) => void,
  onLineEvent: (event: { readonly lineId: string; readonly eventId: string }) => void,
  onScan: (scan: { readonly transferRequestId: string; readonly lineId: string }) => void,
): void {
  const onLineChangeRef = useRef(onLineChange);
  const onLineEventRef = useRef(onLineEvent);
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onLineChangeRef.current = onLineChange;
  }, [onLineChange]);

  useEffect(() => {
    onLineEventRef.current = onLineEvent;
  }, [onLineEvent]);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => {
    const realtimeClient = getSupabaseRealtimeClient();
    if (!realtimeClient) return undefined;

    const applyScan = (message: unknown): void => {
      const parsed = parseTransferScanInsert(message);
      if (parsed == null) return;
      onScanRef.current(parsed);
    };

    const broadcastChannel: RealtimeChannel = realtimeClient.channel(TRANSFER_PICKS_REALTIME_TOPIC, {
      config: { broadcast: { self: true } },
    });
    broadcastChannel.on('broadcast', { event: TRANSFER_LINE_CHANGED_BROADCAST_EVENT }, (message: unknown) => {
      const parsed = parseTransferLineChange(message);
      if (parsed == null) return;
      onLineChangeRef.current(parsed);
    });
    broadcastChannel.on('broadcast', { event: TRANSFER_SCAN_BROADCAST_EVENT }, applyScan);
    subscribeChannel(broadcastChannel, 'picks-broadcast');

    const lineEventsChannel: RealtimeChannel = realtimeClient.channel(`${TRANSFER_PICKS_REALTIME_TOPIC}-line-events`);
    lineEventsChannel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'inventory',
        table: 'transfer_request_line_events',
      },
      (message: unknown) => {
        const parsed = parseTransferLineEventInsert(message);
        if (parsed == null) return;
        onLineEventRef.current(parsed);
      },
    );
    subscribeChannel(lineEventsChannel, 'picks-line-events');

    const scansChannel: RealtimeChannel = realtimeClient.channel(`${TRANSFER_PICKS_REALTIME_TOPIC}-scans`);
    scansChannel.on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'inventory',
        table: 'transfer_request_scans',
      },
      applyScan,
    );
    subscribeChannel(scansChannel, 'picks-scans');

    return () => {
      realtimeClient.removeChannel(broadcastChannel).catch(() => {
        // no-op cleanup
      });
      realtimeClient.removeChannel(lineEventsChannel).catch(() => {
        // no-op cleanup
      });
      realtimeClient.removeChannel(scansChannel).catch(() => {
        // no-op cleanup
      });
    };
  }, []);
}
